import {
    Component,
    DestroyRef,
    OnDestroy,
    OnInit,
    Renderer2,
    Signal,
    TemplateRef,
    effect,
    inject,
    signal,
} from '@angular/core'
import { NavigationEnd, NavigationStart, Router, RouterModule } from '@angular/router'
import { Observable, filter, map, tap } from 'rxjs'
import { SignalRService } from '../core/services/signalr/signalr-service'
import { ToastrService } from 'ngx-toastr'
import { StaffService } from '../core/services/staff/staff.service'
import { LoadingSpinnerComponent } from '../shared/components/loading-spinner/loading-spinner.component'
import { NgbDropdownModule, NgbModal, NgbModule, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap'
import { HeaderComponent } from './secure-components/header/header.component'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AppStore } from '../core/app.store'
import { Me } from '../shared/interfaces/me/me.interface'
import {
    NgxPermissionsConfigurationService,
    NgxPermissionsModule,
    NgxPermissionsService,
    NgxRolesService,
} from 'ngx-permissions'
import { patchState } from '@ngrx/signals'
import { ButtonComponent } from '../shared/components/form-elements/button/button.component'
import { ReleaseNotesService } from '../core/services/release-notes/release-notes.service'
import { EntityType } from '../shared/signalr/change-notification.interface'
import { PostUpdateEvent } from '../shared/signalr/post-update-event.interface'
import { Notice } from '../shared/interfaces/notices/notice.interface'
import { SidebarComponent } from '../shared/components/sidebar/sidebar/sidebar.component'
import { SidebarHeaderComponent } from '../shared/components/sidebar/sidebar-header/sidebar-header.component'
import { SidebarNavComponent } from '../shared/components/sidebar/sidebar-nav/sidebar-nav.component'

import { secureNavItems } from './secure-nav'
import { NavItem } from '../shared/components/sidebar/nav-item.interface'

import { AuthService } from '@auth0/auth0-angular'

import { LoadingRequestCounterService } from '../core/interceptors/loading-request-counter.service'
import { environment } from '../../environments/environment'
import { getAuth0ClaimKey } from '../core/helpers/get-auth0-claim-key'
import { OnboardingChecklistComponent } from '../shared/components/onboarding-checklist/onboarding-checklist.component'
import { OnboardingService } from '../core/services/onboarding/onboarding.service'
import { MonitoringService } from '../core/services/monitoring.service'
import { SidebarStarterComponent } from '../shared/components/sidebar-starter/sidebar-starter.component'
import { SidebarStarterNavComponent } from '../shared/components/sidebar-starter/sidebar-starter-nav/sidebar-starter-nav.component'
import { SidebarStarterUserIconComponent } from '../shared/components/sidebar-starter/sidebar-starter-user-icon/sidebar-starter-user-icon.component'
import { SidebarStarterFooterComponent } from '../shared/components/sidebar-starter/sidebar-starter-footer/sidebar-starter-footer.component'
import { CustomerService } from '../core/services/customer.service'
import { SidebarFooterComponent } from '../shared/components/sidebar/sidebar-footer/sidebar-footer.component'
import { SidebarTrialComponent } from '../shared/components/sidebar/sidebar-trial/sidebar-trial.component'
import { ImpersonationService } from '../core/services/support/impersonation.service'
import { GeneratedReportUpdateEvent } from '../shared/signalr/generated-report-update-event.interface'
import { GeneratedReportStatus } from '../shared/interfaces/reports/generated-reports/generated-report-status.interface'
import { SidebarNavActionService } from '../shared/components/sidebar/sidebar-nav-action.service'
import { RecentCustomersPulloutComponent } from '../shared/components/customer/recent-customers-pullout/recent-customers-pullout.component'

@Component({
    selector: 'sf-secure',
    templateUrl: './secure.component.html',
    styleUrls: ['./secure.component.scss'],
    imports: [
        ButtonComponent,
        RouterModule,
        NgbModule,
        NgxPermissionsModule,
        NgbDropdownModule,
        FontAwesomeModule,
        HeaderComponent,
        LoadingSpinnerComponent,
        SidebarStarterComponent,
        SidebarStarterUserIconComponent,
        SidebarStarterNavComponent,
        SidebarStarterFooterComponent,
        SidebarComponent,
        SidebarHeaderComponent,
        SidebarFooterComponent,
        SidebarTrialComponent,
        SidebarNavComponent,
        OnboardingChecklistComponent,
    ],
})
export class SecureComponent implements OnInit, OnDestroy {
    public counter = inject(LoadingRequestCounterService)

    public appStore = inject(AppStore)
    public authService = inject(AuthService)
    #customerService = inject(CustomerService)
    private destroyRef = inject(DestroyRef)
    private modalService = inject(NgbModal)
    private offcanvas = inject(NgbOffcanvas)
    private router = inject(Router)
    private signalRService = inject(SignalRService)
    private toastr = inject(ToastrService)
    private staffService = inject(StaffService)
    private permissionsConfigurationService = inject(NgxPermissionsConfigurationService)
    private permissionService = inject(NgxPermissionsService)
    private roleService = inject(NgxRolesService)
    private renderer2 = inject(Renderer2)
    private releaseNotesService = inject(ReleaseNotesService)
    public onboardingService = inject(OnboardingService)
    private monitoringService = inject(MonitoringService)
    #impersonationService = inject(ImpersonationService)
    #navActionService = inject(SidebarNavActionService)

    private allNavItems: NavItem[] = [...secureNavItems]
    public filteredNavItems: NavItem[] = [...secureNavItems]

    public environment = environment

    public environmentOptions = [
        { name: 'Local', url: 'http://localhost:4500' },
        { name: 'Development', url: 'https://dev-spa.dev-wb-solutions.co.uk/' },
        { name: 'Staging', url: 'https://stage-spa.dev-wb-solutions.co.uk/' },
        { name: 'Production', url: 'https://spa.surefiresoftware.co.uk' },
    ]

    title = ''

    // Drag functionality properties
    isDragging = false
    dragPosition = { x: 350, y: 10 } // Initial position
    dragOffset = { x: 0, y: 0 }

    onlineUsersViewingCurrentPage: number[] = []

    isLoading!: Observable<boolean>
    apiLoaded!: Observable<boolean>

    me: Signal<Me | null> = this.appStore.me
    companySettings = this.appStore.companySettings

    constructor() {
        effect(() => {
            this.appStore.postCounter()
            this.updateNavCounts()
        })
    }

    userLoadedEffect = effect(() => {
        const roles = this.appStore.roles()
        const permissions = this.appStore.permissions()
        this.filteredNavItems = [...this.buildNavWithPermissions(this.allNavItems, roles, permissions)]
    })

    async ngOnInit(): Promise<void> {
        this.authService.user$.subscribe((user) => {
            if (user) {
                this.monitoringService.appInsights.setAuthenticatedUserContext(
                    user[getAuth0ClaimKey('userId')] || 'anonymous',
                    user[getAuth0ClaimKey('company')] || 'unknown',
                    true
                )
            } else {
                this.authService.loginWithRedirect()
            }
        })

        this.appStore.loadMe()
        this.appStore.loadCompany()
        this.appStore.loadCompanySettings()

        this.#customerService.getRecentlyViewedCustomerCount().subscribe((count) => {
            this.appStore.setRecentViewedCustomersCount(count)
        })

        this.permissionsConfigurationService.addPermissionStrategy(
            'disable',
            (templateRef: TemplateRef<any> | undefined) => {
                this.renderer2.addClass(templateRef, 'disabled')
                this.renderer2.addClass(templateRef?.elementRef.nativeElement.nextSibling, 'disabled')
                if (templateRef?.elementRef.nativeElement.previousSibling) {
                    this.renderer2.addClass(templateRef!.elementRef.nativeElement.previousSibling, 'disabled')
                }
            }
        )

        this.permissionsConfigurationService.addPermissionStrategy('enable', (a: TemplateRef<any> | undefined) => {
            if (a?.elementRef.nativeElement.previousSibling) {
                this.renderer2.removeClass(a!.elementRef.nativeElement.previousSibling, 'disabled')
            }
        })

        await this.signalRService.initiateSignalrConnection()

        this.signalRService
            .getChangesFor<PostUpdateEvent>(EntityType.Post)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((change) => {
                const { postsToProcessCount, urgentPostsToProcessCount, unassignedPostsToProcessCount } =
                    change.data.postCounter

                patchState(this.appStore, {
                    postCounter: {
                        postsToProcessCount: postsToProcessCount,
                        urgentPostsToProcessCount: urgentPostsToProcessCount,
                        unassignedPostsToProcessCount: unassignedPostsToProcessCount,
                    },
                })
            })

        this.signalRService
            .getChangesFor<Notice>(EntityType.Notice)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((change) => {
                const currentCount = this.appStore.unreadNoticesCount()
                this.appStore.setUnreadNoticesCount(currentCount + 1)
                this.showNoticeToastNotification()
            })

        this.signalRService
            .getChangesFor<GeneratedReportUpdateEvent>(EntityType.GeneratedReport)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (change) => {
                    const { name, originalName, status, initiatedByUserId } = change.data

                    if (
                        status === GeneratedReportStatus.Completed &&
                        originalName === null &&
                        initiatedByUserId === this.me()?.id
                    ) {
                        this.toastr
                            .success(
                                `Your "${name}" generated report is ready. Click here to be redirected.`,
                                'Report Ready'
                            )
                            .onTap.subscribe(() => this.router.navigateByUrl('reports/generated-reports'))
                    }
                },
            })

        this.#navActionService.actions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((action) => {
            switch (action) {
                case 'recent-customers':
                    this.offcanvas.open(RecentCustomersPulloutComponent, { position: 'end' })
                    break
            }
        })

        this.setTitle()

        this.router.events
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((event) => event instanceof NavigationStart || event instanceof NavigationEnd),
                tap((event) => {
                    // console.log('Send removal', event)
                    if (event instanceof NavigationStart) {
                        this.onlineUsersViewingCurrentPage = []
                        const previousNavigation = this.router.currentNavigation()
                        const previousUrl = previousNavigation?.previousNavigation?.finalUrl?.toString()
                        if (previousUrl) {
                            //this.signalRService.connection.invoke('LeavePageGroup', previousUrl)
                        }
                    }
                }),
                filter((event) => event instanceof NavigationEnd),
                tap((event) => {
                    // console.log('Send new page', event)
                    if (event instanceof NavigationEnd) {
                        this.modalService.dismissAll()
                        this.offcanvas.dismiss()
                        //this.signalRService.connection.invoke('JoinPageGroup', event.url)
                    }
                }),
                map(() => this.getTitleFromRoute())
            )
            .subscribe((title: string) => {
                this.setTitle()
            })

        this.staffService.getAllStaff().subscribe((staff) => this.appStore.setStaff(staff))

        if (this.me() != null) {
            const { acknowledgedLatestReleaseNote, unreadNoticesCount } = this.me()!

            if (!acknowledgedLatestReleaseNote) {
                this.releaseNotesService.checkForLatestReleaseDialog()
            }

            if (unreadNoticesCount > 0) {
                this.showNoticeToastNotification()
            }
        }
    }

    buildNavWithPermissions(navItems: NavItem[], userRoles: string[], userPermissions: string[]): NavItem[] {
        return navItems
            .map((item) => this.processItem(item, userRoles, userPermissions))
            .filter((i): i is NavItem => i !== null)
    }

    private processItem(item: NavItem, userRoles: string[], userPermissions: string[]): NavItem | null {
        let doesNotHaveRole = false

        if (item.roles) {
            const hasRole = Array.isArray(item.roles)
                ? item.roles.some((role) => userRoles.includes(role))
                : userRoles.includes(item.roles)
            doesNotHaveRole = !hasRole

            console.log(`Item: ${item.name} requires roles ${item.roles}, User has role: ${hasRole}`)

            if (doesNotHaveRole) {
                return null // 🚨 completely remove this item
            }
        }

        let doesNotHavePermission = false

        if (item.permissions) {
            const hasPermission = Array.isArray(item.permissions)
                ? item.permissions.some((permission) => userPermissions.includes(permission))
                : userPermissions.includes(item.permissions)

            doesNotHavePermission = !hasPermission

            if (item.permissionStrategy === 'remove' && doesNotHavePermission) {
                return null // 🚨 completely remove this item
            }

            if (item.permissionStrategy === 'disable') {
                item.attributes = {
                    ...(item.attributes || {}),
                    disabled: doesNotHavePermission,
                }
            }
        }

        // recurse into children
        if (item.children && item.children.length > 0) {
            const processedChildren = item.children.map((child) => this.processItem(child, userRoles, userPermissions))

            // filter out removed children
            item.children = processedChildren.filter((c) => c !== null) as NavItem[]

            // If all children are removed and this item has no url, optionally remove parent too
            if (!item.url && item.children.length === 0) {
                return null
            }
        }

        return { ...item }
    }

    updateNavCounts(): void {
        const { postsToProcessCount, urgentPostsToProcessCount, unassignedPostsToProcessCount } =
            this.appStore.postCounter()
        this.filteredNavItems = this.filteredNavItems.map((item) => {
            if (item.counterKey === 'posts') {
                item.badge = {
                    color: unassignedPostsToProcessCount > 0 ? 'danger' : 'success',
                    text: `${unassignedPostsToProcessCount}` || '0',
                }
            }

            return { ...item }
        })
    }

    showNoticeToastNotification() {
        this.toastr
            .info('Click here to be redirected to the notices page for more information.', 'New Notice Available', {
                timeOut: 0,
            })
            .onTap.subscribe(() => this.router.navigateByUrl('notices'))
    }

    toggleMenu(): void {
        this.appStore.toggleSidebarVisibility()
    }

    switchEnvironment(url: string): void {
        window.location.href = url
    }

    requestChangePassword(): void {
        this.staffService.requestChangePassword(this.me()!.id).subscribe({
            next: (url: string) => {
                this.toastr.success('Password change requested. Please check your email for further instructions.')
                // open in new tab
                window.open(url, '_blank')
            },
            error: (err) => {
                this.toastr.error('There was an error requesting the password change. Please try again later.')
            },
        })
    }

    // Drag functionality methods
    onDragStart(event: MouseEvent): void {
        this.isDragging = true
        const rect = (event.target as HTMLElement).getBoundingClientRect()
        this.dragOffset.x = event.clientX - rect.left
        this.dragOffset.y = event.clientY - rect.top

        document.addEventListener('mousemove', this.onDrag.bind(this))
        document.addEventListener('mouseup', this.onDragEnd.bind(this))

        event.preventDefault()
    }

    onDrag(event: MouseEvent): void {
        if (!this.isDragging) return

        this.dragPosition.x = event.clientX - this.dragOffset.x
        this.dragPosition.y = event.clientY - this.dragOffset.y

        // Constrain to viewport
        const maxX = window.innerWidth - 150 // Approximate width of the dropdown
        const maxY = window.innerHeight - 50 // Approximate height of the dropdown

        this.dragPosition.x = Math.max(0, Math.min(this.dragPosition.x, maxX))
        this.dragPosition.y = Math.max(0, Math.min(this.dragPosition.y, maxY))
    }

    onDragEnd(): void {
        this.isDragging = false
        document.removeEventListener('mousemove', this.onDrag.bind(this))
        document.removeEventListener('mouseup', this.onDragEnd.bind(this))
    }

    // Touch event handlers for mobile devices
    onTouchStart(event: TouchEvent): void {
        this.isDragging = true
        const touch = event.touches[0]
        const rect = (event.target as HTMLElement).getBoundingClientRect()
        this.dragOffset.x = touch.clientX - rect.left
        this.dragOffset.y = touch.clientY - rect.top

        document.addEventListener('touchmove', this.onTouchMove.bind(this))
        document.addEventListener('touchend', this.onTouchEnd.bind(this))

        event.preventDefault()
    }

    onTouchMove(event: TouchEvent): void {
        if (!this.isDragging) return

        const touch = event.touches[0]
        this.dragPosition.x = touch.clientX - this.dragOffset.x
        this.dragPosition.y = touch.clientY - this.dragOffset.y

        // Constrain to viewport
        const maxX = window.innerWidth - 150 // Approximate width of the dropdown
        const maxY = window.innerHeight - 50 // Approximate height of the dropdown

        this.dragPosition.x = Math.max(0, Math.min(this.dragPosition.x, maxX))
        this.dragPosition.y = Math.max(0, Math.min(this.dragPosition.y, maxY))

        event.preventDefault()
    }

    onTouchEnd(): void {
        this.isDragging = false
        document.removeEventListener('touchmove', this.onTouchMove.bind(this))
        document.removeEventListener('touchend', this.onTouchEnd.bind(this))
    }

    stoppedImpersonationClicked = signal(false)
    stopImpersonation(): void {
        this.stoppedImpersonationClicked.set(true)
        this.#impersonationService.stopImpersonation().subscribe({
            next: () => {
                this.toastr.success('Impersonation stopped. You will be redirected shortly.')
                setTimeout(() => {
                    window.location.href = '/diary'
                }, 2000)
            },
            error: () => {
                this.toastr.error('Failed to stop impersonation')
                this.stoppedImpersonationClicked.set(false)
            },
        })
    }

    private setTitle(): void {
        this.title = this.getTitleFromRoute()
    }

    private getTitleFromRoute(): string {
        let route = this.router.routerState.root
        let routeTitle = ''
        while (route!.firstChild) {
            route = route!.firstChild
        }
        if (route.snapshot.title) {
            routeTitle = route.snapshot.title
        }
        return routeTitle
    }

    logout(): void {
        this.signalRService?.endConnection()
        this.authService.logout({
            logoutParams: {
                returnTo: window.location.origin,
            },
        })
    }

    ngOnDestroy(): void {
        this.signalRService?.endConnection()
    }
}
